import React from 'react';
import { Plane, Stethoscope, MapPin, HeartPulse, ShieldCheck, Users, Star } from 'lucide-react';

const Hero = () => {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section id="home" className="relative overflow-hidden min-h-screen flex items-center bg-white">
      {/* Soft background */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-orange-50 via-white to-teal-50 opacity-80" />
        <div className="absolute -top-10 -left-10 w-96 h-96 bg-brand-orange/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-10 -right-10 w-96 h-96 bg-brand-teal/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full py-24">
        
        {/* Location pill */}
        <div className="flex justify-center lg:justify-start mb-6">
          <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-4 py-1.5">
            <MapPin size={13} className="text-brand-teal" />
            <span className="text-brand-teal font-semibold text-sm">Starting in Coimbatore · Expanding across South India</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div className="text-center lg:text-left">
            <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 leading-[1.1] mb-6">
              Going Away?<br/>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-orange to-amber-500">
                Your Pet is Safe.
              </span><br/>
              Emergency?{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-emergency-red to-rose-500">
                Help is Near.
              </span>
            </h1>

            <p className="text-lg md:text-xl text-gray-500 mb-4 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              PetCares is South India's pet care connection platform — built around two real problems every pet owner faces:
            </p>
            <ul className="text-base md:text-lg text-gray-600 mb-10 max-w-xl mx-auto lg:mx-0 space-y-2 text-left">
              <li className="flex items-start gap-3">
                <Plane size={20} className="text-brand-orange flex-shrink-0 mt-0.5" />
                <span><strong className="text-gray-900">When you travel or step out</strong> — instantly find trusted local pet shops that care for your pet so you never worry.</span>
              </li>
              <li className="flex items-start gap-3">
                <Stethoscope size={20} className="text-emergency-red flex-shrink-0 mt-0.5" />
                <span><strong className="text-gray-900">When it's an emergency</strong> — connect directly with verified, licensed vets near you in minutes, not hours.</span>
              </li>
            </ul>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button
                onClick={() => scrollTo('petshop')}
                className="flex items-center justify-center gap-2 px-8 py-4 bg-brand-dark text-white rounded-full font-bold text-lg hover:bg-gray-800 transition-all transform hover:-translate-y-1 shadow-xl"
              >
                <Plane size={20} /> Find Pet Boarding
              </button>
              <button
                onClick={() => scrollTo('emergency')}
                className="flex items-center justify-center gap-2 px-8 py-4 bg-white text-emergency-red border-2 border-emergency-red rounded-full font-bold text-lg hover:bg-red-50 transition-all transform hover:-translate-y-1 shadow-lg"
              >
                <HeartPulse size={20} /> Emergency Vet
              </button>
            </div>

            {/* Trust row */}
            <div className="flex flex-wrap gap-5 justify-center lg:justify-start mt-10 pt-10 border-t border-gray-100">
              {[
                { icon: <ShieldCheck size={18} className="text-brand-teal" />, label: 'Verified Partners Only' },
                { icon: <Users size={18} className="text-brand-orange" />, label: '500+ Pets Cared For' },
                { icon: <HeartPulse size={18} className="text-emergency-red" />, label: '24/7 Vet Access' },
                { icon: <MapPin size={18} className="text-purple-500" />, label: 'Coimbatore · South India' },
              ].map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-gray-600 font-medium">
                  {t.icon} {t.label}
                </div>
              ))}
            </div>
          </div>

          {/* Right — Visual (Single Featured on Mobile / Masonry Collage on Desktop) */}
          <div className="relative h-80 lg:h-full lg:min-h-[550px] mt-12 lg:mt-0">
            {/* Mobile-only featured image */}
            <div className="lg:hidden w-full h-full rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white relative group">
              <img src={require('../assets/images/boarding_hero.png')} alt="Premium Pet Care" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent p-5 pt-20">
                <div className="flex items-center gap-2 mb-1">
                  <Star className="text-yellow-400" size={16} fill="currentColor" />
                  <span className="text-white font-bold text-sm">4.9/5 Rating</span>
                </div>
                <p className="text-gray-300 text-xs">Trusted Pet Boarding Across South India</p>
              </div>
            </div>

            {/* Desktop-only masonry collage */}
            <div className="hidden lg:block absolute inset-0">
            {/* Pattern Background */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 z-0" />
            
            {/* Top Right: Vet Image */}
            <div className="absolute top-0 right-0 w-[85%] h-72 rounded-3xl overflow-hidden shadow-2xl border-8 border-white z-10 group hover:-translate-y-2 transition-transform duration-500 bg-gray-100">
              <img src={require('../assets/images/vet_hero.png')} alt="Emergency Vet Clinic" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-md p-3.5 rounded-2xl shadow-xl flex items-center gap-3 transform group-hover:-translate-y-1 transition-transform">
                <div className="w-11 h-11 bg-red-50 rounded-xl flex items-center justify-center shadow-inner">
                  <Stethoscope className="text-emergency-red animate-pulse" size={22} />
                </div>
                <div>
                  <p className="font-extrabold text-gray-900 text-[15px]">Licensed Emergency Vets</p>
                  <p className="text-xs font-semibold text-gray-500 flex items-center gap-1.5 mt-0.5"><span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> Available right now</p>
                </div>
              </div>
            </div>

            {/* Bottom Left: Boarding Image */}
            <div className="absolute bottom-4 left-0 w-[75%] h-64 rounded-3xl overflow-hidden shadow-2xl z-20 group hover:-translate-y-2 transition-transform duration-500 border-8 border-white bg-gray-100">
              <img src={require('../assets/images/boarding_hero.png')} alt="Premium Pet Boarding" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute top-5 right-5 bg-white/95 backdrop-blur-md px-4 py-2 rounded-full shadow-xl flex items-center gap-2 transform group-hover:scale-105 transition-transform">
                <Star className="text-yellow-500" size={16} fill="currentColor" />
                <span className="font-extrabold text-sm text-gray-900">4.9/5 Rating</span>
              </div>
              <div className="absolute bottom-5 left-5 bg-brand-dark/80 backdrop-blur-md px-4 py-2 rounded-xl text-white">
                <p className="font-bold text-sm">Trusted Boarding</p>
                <p className="text-xs text-gray-300">Monitored 24/7</p>
              </div>
            </div>
            
            {/* Bottom Right: Community Image Circle */}
            <div className="absolute -bottom-6 -right-4 w-44 h-44 rounded-full overflow-hidden shadow-2xl z-30 border-8 border-white group hover:scale-105 transition-transform duration-500 bg-gray-100">
              <img src={require('../assets/images/community.png')} alt="Happy Pet Owner" className="w-full h-full object-cover" />
            </div>
            
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
